import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBillingStatus } from '../hooks/useBilling';

// 定数定義
const URGENT_DAYS_THRESHOLD = 3; // 緊急警告に切り替える日数の閾値

/**
 * 支払い遅延時の警告バナーコンポーネント
 * past_due状態の時に全画面上部に表示
 */
export const PaymentWarningBanner: React.FC = () => {
  const navigate = useNavigate();
  const { isPastDue, pastDueInfo } = useBillingStatus();

  // past_due状態でない場合は表示しない
  if (!isPastDue || !pastDueInfo) {
    return null;
  }

  const { daysRemaining, gracePeriodDays } = pastDueInfo;
  const isUrgent = daysRemaining <= URGENT_DAYS_THRESHOLD;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] ${
        isUrgent
          ? 'bg-gradient-to-r from-red-600 to-red-700'
          : 'bg-gradient-to-r from-orange-500 to-orange-600'
      } text-white shadow-lg`}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* 警告アイコン */}
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 animate-pulse"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* メッセージ */}
            <div className="flex-1">
              <p className="font-bold text-sm sm:text-base">
                ⚠️ お支払いが確認できません
              </p>
              <p className="text-xs sm:text-sm opacity-90 mt-1">
                {daysRemaining > 0 ? (
                  <>
                    現在、サービスの利用が制限されています。
                    <span className="font-bold mx-1">
                      残り{daysRemaining}日
                    </span>
                    以内にお支払いを完了してください。
                  </>
                ) : (
                  <>
                    お支払いの猶予期間が終了しました。直ちにお支払いを完了してください。
                  </>
                )}
              </p>
            </div>
          </div>

          {/* アクションボタン */}
          <div className="flex-shrink-0 ml-4">
            <button
              onClick={() => navigate('/billing')}
              className="bg-white text-gray-900 px-4 py-2 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors duration-200 shadow-md"
            >
              お支払い手続きへ
            </button>
          </div>
        </div>

        {/* 制限内容の詳細 */}
        <div className="mt-2 text-xs opacity-90 border-t border-white border-opacity-20 pt-2">
          <p>
            ⛔ 現在の制限: 新規評価の実行、ユーザー管理、バッチ処理が利用できません（既存データの閲覧は可能）
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentWarningBanner;
